import React from 'react';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IActualDepositData } from './MatchedDepositsType';
import {
  useDeleteMatchACHIncomingMutation,
  useDeleteMatchChecksIncomingMutation,
  useDeleteMatchWiresIncomingMutation,
} from 'API/accounting/AccountingApi';
import { assertDefined } from 'utilities/Assertions';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { MatchedDepositRowDisplay } from './MatchedDepositRowDisplay';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

interface IMatchedDepositRowProps {
  depositData: IActualDepositData;
  index: number;
}

const permissionFromType = (x: string) => {
  switch (x) {
    case 'Check':
      return [ePermissions.EscrowActualIncomingChecksEdit, ePermissions.EscrowActualIncomingChecksRead];
    case 'Wire':
      return [ePermissions.EscrowActualIncomingWiresEdit, ePermissions.EscrowActualIncomingWiresRead];
    case 'ACH':
      return [ePermissions.EscrowActualAchDepositsEdit, ePermissions.EscrowActualAchDepositsRead];
    default:
      return [];
  }
};

export const MatchedDepositRowContainer: React.FC<IMatchedDepositRowProps> = ({ depositData, index }) => {
  const permissionArray = permissionFromType(depositData.type);
  const hasEditPermission = useHasPermissions(permissionArray, true);
  const [deleteCheckMatch] = useDeleteMatchChecksIncomingMutation();
  const [deleteWireMatch] = useDeleteMatchWiresIncomingMutation();
  const [deleteACHMatch] = useDeleteMatchACHIncomingMutation();

  const onUnmatchCheck = async () => {
    assertDefined(depositData.expectedCheckId);
    assertDefined(depositData.actualCheckId);
    const result = await deleteCheckMatch({
      actualId: depositData.actualCheckId,
      expectedId: depositData.expectedCheckId,
    });
    checkForMutationSuccess(result, 'unmatching check');
  };

  const onUnmatchWire = async () => {
    assertDefined(depositData.expectedWireId);
    assertDefined(depositData.actualWireId);
    const result = await deleteWireMatch({
      actualId: depositData.actualWireId,
      expectedId: depositData.expectedWireId,
    });
    checkForMutationSuccess(result, 'unmatching wire');
  };

  const onUnmatchAch = async () => {
    assertDefined(depositData.expectedAchId);
    assertDefined(depositData.actualAchId);
    const result = await deleteACHMatch({
      actualId: depositData.actualAchId,
      expectedId: depositData.expectedAchId,
    });
    checkForMutationSuccess(result, 'unmatching ach');
  };

  const onUnmatch = () => {
    switch (depositData.type) {
      case 'Check':
        return onUnmatchCheck();
      case 'Wire':
        return onUnmatchWire();
      case 'ACH':
        return onUnmatchAch();
      default:
        throw new Error(
          `Error while unmatching: depositData must be of type 'Check', 'Wire', or 'ACH' but was ${depositData.type}`
        );
    }
  };

  return (
    <MatchedDepositRowDisplay
      onUnmatch={onUnmatch}
      depositData={depositData}
      index={index}
      hasEditPermission={hasEditPermission}
    />
  );
};
